import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    Event,
    NavigationCancel,
    NavigationCancellationCode,
    NavigationEnd,
    Router,
} from '@angular/router';

/** Error routes: arriving on one is a failure, never a page to return to. */
const ERROR_ROUTE_PREFIXES = ['/errors', '/forbidden'];

/** Enough to find a working page; older entries are never the answer. */
const MAX_VISITS = 50;

/** Where "back" goes when nothing usable is left: resolves to the role home or sign-in. */
const FALLBACK_URL = '/';

export function isErrorRouteUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return ERROR_ROUTE_PREFIXES.some(
        (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
}

/**
 * The "Quay lại" button of the 404/403 pages, without the back-loop.
 *
 * `history.back()` returned to the URL that had just failed, which failed again and
 * reopened the error page. The loop had no exit in two common cases:
 * - the bad link was the tab's first page: the initial navigation's guard redirect
 *   pushes `/forbidden` on top of the bad URL;
 * - a page's own request sent the user here: nera-common's page-level 403 redirect,
 *   a public album with no id.
 *
 * Instead this tracks the tab's in-app navigations, remembers which URL failed each
 * time an error route is reached, and goes to the newest page that worked. When there
 * is none (direct entry, or only failed pages), it goes to {@link FALLBACK_URL}.
 * Eagerly created in app.config.ts so the initial navigation is recorded too.
 */
@Injectable({ providedIn: 'root' })
export class SafeBackService {
    private readonly _router = inject(Router);
    private readonly _visits: string[] = [];
    private readonly _failed = new Set<string>();
    /** Target of the navigation a guard just redirected away from (usually to an error route). */
    private _redirectedFrom: string | null = null;

    constructor() {
        this._router.events
            .pipe(takeUntilDestroyed())
            .subscribe((event) => this._record(event));
    }

    /** Leaves the current error page for the newest page that worked, or the home fallback. */
    goBack(): void {
        const current = this._router.url;
        // Not an error route: an error rendered in place at the bad URL (InShellNotFound).
        if (!isErrorRouteUrl(current)) this._failed.add(current);

        const target = [...this._visits]
            .reverse()
            .find((url) => !isErrorRouteUrl(url) && !this._failed.has(url));
        // replaceUrl: the error page leaves the browser history, so the browser's own back
        // button doesn't return to it either.
        void this._router.navigateByUrl(target ?? FALLBACK_URL, {
            replaceUrl: true,
        });
    }

    private _record(event: Event): void {
        if (
            event instanceof NavigationCancel &&
            event.code === NavigationCancellationCode.Redirect
        ) {
            this._redirectedFrom = event.url;
            return;
        }
        if (!(event instanceof NavigationEnd)) return;

        const landed = event.urlAfterRedirects;
        if (isErrorRouteUrl(landed)) {
            const failed = this._failedUrl(event);
            if (failed && !isErrorRouteUrl(failed)) this._failed.add(failed);
        }
        this._redirectedFrom = null;

        this._visits.push(landed);
        if (this._visits.length > MAX_VISITS) this._visits.shift();
    }

    /**
     * Which URL sent the user to the error route:
     * - a guard redirect cancels the requested navigation first, so it is that URL;
     * - a route-config `redirectTo` keeps it as the event's `url`;
     * - otherwise the previous page's own request failed (API 403/404), so it is that page.
     */
    private _failedUrl(event: NavigationEnd): string | undefined {
        if (this._redirectedFrom) return this._redirectedFrom;
        if (event.url !== event.urlAfterRedirects) return event.url;
        return this._visits.at(-1);
    }
}
